import React, { useEffect, useState } from 'react';
import map from 'lodash/map';
import Button from 'app/shared/core/Button';
import ContactCallToAction from 'app/shared/modules/photo-gallery/ContactCallToAction';
import PhotoCarouselV2 from 'app/shared/modules/PhotoCarouselV2';
import Tooltip from 'app/shared/modules/Tooltip';
import ImageLoader from 'app/shared/modules/photo-gallery/ImageLoader';
import Icon3DGlasses from 'images/icons/3d-glasses.svg';
import IconVerified from 'images/verified.svg';
import noPhoto from 'images/noPhoto.png';
import {
  listingHasTourUrls,
  listingUtils_getListingImgAltText,
  listingUtils_getListingImgTitleText,
} from 'app/shared/utils/listingUtils';
import {
  HdpPhotoGalleryContent,
  HdpPhotoGalleryListingAgent,
  HdpPhotoGalleryTagIcon,
  hdpPhotoGalleryTagStyles,
  HdpPhotoGalleryTagWrapper,
  ImagePlaceholder,
  NoPhoto,
} from './HdpPhotoGallery.styles';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type Photo from 'app/shared/models/Photo';

interface GalleryViewProps {
  listing: ListingDetails;
  photos: Array<Photo>;
  isLoadingAllPhotos: boolean;
  statePhotoIdx: number;
  isMediumOrSmallerScreenSize: boolean;
  ignoreKeypress: boolean;
  shouldBeHiddenIfDataCannotBeRetained: boolean;
  showContactInfo: boolean;
  onPhotoCarouselChange: (index: number) => void;
  onOpenPhotoGalleryFull: () => void;
  onOpen3dTours: () => void;
  onClickContact?: () => void;
}

const VERIFIED_TEXT =
  'This listing comes to HotPads from a verified source, ' +
  "a known and established property manager or property manager's representative.";

const INITIAL_PHOTOS_TO_LOAD = 5;
const PHOTOS_TO_LOAD_PER_BATCH = 5;
const BATCH_LOAD_DELAY = 0;

const GalleryView = React.memo(
  ({
    listing,
    photos,
    isLoadingAllPhotos,
    statePhotoIdx,
    isMediumOrSmallerScreenSize,
    ignoreKeypress,
    shouldBeHiddenIfDataCannotBeRetained,
    showContactInfo,
    onPhotoCarouselChange,
    onOpenPhotoGalleryFull,
    onOpen3dTours,
    onClickContact,
  }: GalleryViewProps) => {
    const { active, address, listedBy, trusted, displayName } = listing;
    const show3dToursButton = listingHasTourUrls(listing);
    const maxIndex = photos.length;

    // Progressive loading state - load only first photo for optimal LCP
    const [visiblePhotoCount, setVisiblePhotoCount] = useState(isLoadingAllPhotos ? INITIAL_PHOTOS_TO_LOAD : 1);

    // Preload images near current index
    useEffect(() => {
      if (!isLoadingAllPhotos || photos.length === 0) return;

      // Preload images around current index
      const preloadRadius = 2;
      const preloadImages: Array<HTMLImageElement> = [];

      for (let i = -preloadRadius; i <= preloadRadius; i++) {
        const idx = statePhotoIdx + i;
        if (idx >= 0 && idx < photos.length && idx !== statePhotoIdx) {
          const img = new Image();
          img.src = photos[idx].url;
          preloadImages.push(img);
        }
      }

      return () => {
        preloadImages.forEach((img) => {
          img.src = '';
        });
      };
    }, [statePhotoIdx, photos, isLoadingAllPhotos]);

    useEffect(() => {
      if (!isLoadingAllPhotos || visiblePhotoCount >= photos.length) return;

      // Load second image quickly after first, then continue with batches
      const delay = visiblePhotoCount === 1 ? 50 : BATCH_LOAD_DELAY;

      const timer = setTimeout(() => {
        setVisiblePhotoCount((prevCount) => Math.min(prevCount + PHOTOS_TO_LOAD_PER_BATCH, photos.length));
      }, delay);

      return () => clearTimeout(timer);
    }, [isLoadingAllPhotos, visiblePhotoCount, photos.length]);

    const handlePhotoChange = (index: number) => {
      if (index >= visiblePhotoCount) {
        setVisiblePhotoCount(Math.min(index + PHOTOS_TO_LOAD_PER_BATCH, photos.length));
      }
      onPhotoCarouselChange(index);
    };

    return (
      <HdpPhotoGalleryContent data-elementtiming="HdpPhotoGalleryV2-Carousel">
        {shouldBeHiddenIfDataCannotBeRetained ? (
          <NoPhoto src={noPhoto} alt="This expired listing does not have any photos" />
        ) : (
          <>
            <PhotoCarouselV2
              animateChange={isMediumOrSmallerScreenSize}
              defaultIndex={statePhotoIdx}
              ignoreKeypress={ignoreKeypress}
              itemCountOffset={-1}
              itemCountPosition="top-right"
              loopable
              maxIndex={maxIndex}
              onChange={handlePhotoChange}
            >
              {map(photos, (photo, idx) => {
                // Always render first image for LCP, others based on visiblePhotoCount
                const shouldRenderImage = idx === 0 || idx < visiblePhotoCount;
                const photoCaption = photo.caption;
                const imgAltText = listingUtils_getListingImgAltText({
                  photoCaption,
                  nameOrAddress: displayName,
                  isPrimaryPhoto: idx === 0,
                });
                const imgTitleText = listingUtils_getListingImgTitleText({
                  currentIndex: idx,
                  maxIndex,
                  nameOrAddress: displayName,
                  addressDetails: address,
                });

                if (!shouldRenderImage) {
                  return (
                    <ImagePlaceholder key={`photo-${idx}`} onClick={onOpenPhotoGalleryFull}>
                      <span style={{ color: '#999' }}>Loading...</span>
                    </ImagePlaceholder>
                  );
                }

                return (
                  <ImageLoader
                    image={photo.url}
                    key={`photo-${idx}`}
                    onClick={onOpenPhotoGalleryFull}
                    altText={imgAltText}
                    titleText={imgTitleText}
                    fetchPriority={idx === 0 ? 'high' : idx <= 2 ? 'low' : 'auto'}
                    isLcpImage={idx === 0}
                    loading={idx === 0 ? 'eager' : 'lazy'}
                  />
                );
              })}
              {onClickContact && listing.active && (
                <div key="HdpPhotoGallery-contact-overlay">
                  <ContactCallToAction onCtaClick={onClickContact} />
                </div>
              )}
            </PhotoCarouselV2>
            {showContactInfo && (
              <HdpPhotoGalleryListingAgent>
                {`${listedBy.contactName}: ${listedBy.contactPhone}`}
              </HdpPhotoGalleryListingAgent>
            )}
            <HdpPhotoGalleryTagWrapper>
              {show3dToursButton && (
                <Button
                  className={hdpPhotoGalleryTagStyles}
                  size="xs"
                  btnType="default"
                  rounded
                  onClick={onOpen3dTours}
                >
                  <HdpPhotoGalleryTagIcon alt="" src={Icon3DGlasses} />
                  <span>3D Tour</span>
                </Button>
              )}
              {active && trusted && (
                <Button className={hdpPhotoGalleryTagStyles} size="xs" btnType="default" rounded>
                  <Tooltip tip={VERIFIED_TEXT} tooltipPosition="below">
                    <HdpPhotoGalleryTagIcon alt="" src={IconVerified} />
                    <span>Verified</span>
                  </Tooltip>
                </Button>
              )}
            </HdpPhotoGalleryTagWrapper>
          </>
        )}
      </HdpPhotoGalleryContent>
    );
  },
);

GalleryView.displayName = 'GalleryView';

export default GalleryView;
